<template>
  <div class="ac-quality">
    <div class="ac-head">
      <h1>🤖 Auto-Confirm Quality <span class="build-stamp" :title="'Deployed build marker — if missing or old, the frontend was not rebuilt'">{{ buildStamp }}</span></h1>
      <p class="sub">What the AI decided vs. what really happened — so you can trust it before going live.</p>
      <button class="refresh" @click="load" :disabled="loading">{{ loading ? 'Loading…' : '↻ Refresh' }}</button>
    </div>

    <!-- full-page spinner only on the FIRST load (no data yet) -->
    <div v-if="isAdmin !== true || (loading && !data)" class="loading">
      <span class="spinner big"></span> Loading…
    </div>

    <!-- ═══ TABS ═══ -->
    <div v-else class="ac-tabs">
      <button class="ac-tab" :class="{on: activeTab==='summary'}" @click="activeTab='summary'">📊 Summary</button>
      <button class="ac-tab" :class="{on: activeTab==='details'}" @click="activeTab='details'">🔍 Details &amp; orders</button>
    </div>

    <!-- Reload indicator — shows on warehouse switch / refresh (when data already exists) -->
    <transition name="fade">
      <div v-if="isAdmin === true && loading && data" class="reload-bar">
        <span class="spinner"></span> Mise à jour pour <b>{{ whLabel || 'ce warehouse' }}</b>…
      </div>
    </transition>

    <!-- Configured threshold for the header-selected warehouse — page-level banner -->
    <div class="whthr-banner" v-if="isAdmin === true && whThreshold != null && !(loading && data)">
      <span class="whthr-star">⭐</span>
      <span>Votre seuil pour <b>{{ whLabel }}</b> :
        <b class="whthr-val">{{ Math.round(whThreshold * 100) }}%</b>
        <span class="whthr-hint">— surligné dans le tableau des seuils. Modifiable dans <b>Réglages → 🤖 AI Agent</b>.</span>
      </span>
    </div>

    <!-- Address gate setting for the header-selected warehouse -->
    <div class="whthr-banner" :class="{ 'whaddr-off': whRequireAddress === false }"
         v-if="isAdmin === true && whRequireAddress != null && !(loading && data)">
      <span class="whthr-star">{{ whRequireAddress ? '🛡️' : '⚠️' }}</span>
      <span v-if="whRequireAddress">Adresse de livraison pour <b>{{ whLabel }}</b> :
        <b class="whthr-val">OBLIGATOIRE</b>
        <span class="whthr-hint">— l'IA ne confirme jamais une commande sans adresse utilisable (elle essaie d'abord l'adresse connue du client ; sinon la commande reste en attente pour un appel). Modifiable dans <b>Réglages → 🤖 AI Agent</b>.</span>
      </span>
      <span v-else>Adresse de livraison pour <b>{{ whLabel }}</b> :
        <b class="whaddr-val-off">NON REQUISE</b>
        <span class="whthr-hint">— l'IA peut confirmer des commandes sans adresse (« - »), qui ne sont pas livrables. Activez-la dans <b>Réglages → 🤖 AI Agent</b>.</span>
      </span>
    </div>

    <!-- ═══════════ SUMMARY TAB ═══════════ -->
    <div v-if="isAdmin === true && !(loading && !data) && activeTab==='summary'">
      <!-- ═══ REAL-DATA QUALITY (backtest on historical orders) ═══ -->
      <div v-if="bt" class="realbox">
        <div class="realhead collapsible" @click="showRealQuality = !showRealQuality">
          <h2>
            <span class="caret">{{ showRealQuality ? '▼' : '▶' }}</span>
            📈 Real Quality — tested on {{ bt.totalOrders.toLocaleString() }} historical orders
            <span class="muted sm" v-if="!showRealQuality">— {{ bt.recommended.precision }}% precision (click to open)</span>
          </h2>
          <p class="sub" v-if="showRealQuality">This is the AI's true accuracy on real past orders (delivered vs failed). Baseline (confirm everyone): <b>{{ bt.baselineDeliverRate }}%</b>.</p>
        </div>
        <div v-show="showRealQuality">
        <div class="cards">
          <div class="card">
            <div class="big">{{ bt.recommended.precision }}%</div>
            <div class="lbl">Precision @ {{ bt.recommended.threshold }}%</div>
            <div class="note">of auto-confirms that reached Processed (cash collected)</div>
          </div>
          <div class="card">
            <div class="big">{{ bt.recommended.autoConfirm.toLocaleString() }}</div>
            <div class="lbl">Would auto-confirm</div>
            <div class="note">calls saved at this bar</div>
          </div>
          <div class="card warn">
            <div class="big">{{ bt.recommended.mistakes.toLocaleString() }}</div>
            <div class="lbl">Mistakes</div>
            <div class="note">auto-confirmed but failed (refused / returned)</div>
          </div>
          <div class="card">
            <div class="big">+{{ (bt.recommended.precision - bt.baselineDeliverRate).toFixed(1) }}pt</div>
            <div class="lbl">Better than baseline</div>
            <div class="note">vs confirming blindly</div>
          </div>
        </div>

        <div class="cols">
          <div>
            <h3>Pick your threshold (real data)</h3>
            <table class="thr">
              <thead><tr><th>Bar</th><th>Auto-confirm</th><th>Orders</th><th>Precision</th></tr></thead>
              <tbody>
                <tr v-for="t in bt.thresholds" :key="t.threshold" :class="{ 'thr-selected': isWhThreshold(t.threshold) }">
                  <td>≥ {{ t.threshold }}% <span v-if="isWhThreshold(t.threshold)" class="thr-badge">votre seuil</span></td>
                  <td>{{ t.coverage }}%</td>
                  <td>{{ t.count.toLocaleString() }}</td>
                  <td :class="precClass(t.precision)">{{ t.precision != null ? t.precision + '%' : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h3>By country (@ 85%)</h3>
            <table class="thr">
              <thead><tr><th>Country</th><th>Auto-confirm</th><th>Precision</th><th>Orders</th></tr></thead>
              <tbody>
                <tr v-for="c in bt.byCountry" :key="c.country">
                  <td>{{ c.country }}</td>
                  <td>{{ c.coverage }}%</td>
                  <td :class="precClass(c.precision)">{{ c.precision }}%</td>
                  <td>{{ c.n.toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div><!-- /v-show showRealQuality -->
      </div>
      <div v-else class="realbox muted-box">
        Real-data quality not available yet — run the backtest to populate it.
      </div>

      <hr class="divide" />

      <!-- ═══ AUTO-CONFIRM ACCURACY — clean, scannable ═══ -->
      <div class="acc-panel" v-if="data && aco">
        <div class="acc-head">
          <h3>🎯 AI said <b>Confirm</b> on {{ aco.total.toLocaleString() }} orders</h3>
          <p class="sub">Here is what actually happened to them. <b>Processed</b> = cash collected → the AI was right.</p>
        </div>

        <!-- outcome cards -->
        <div class="acc-buckets">
          <div class="acc-bucket b-teal" v-if="aco.callCenterConfirmed != null">
            <div class="acc-num">{{ aco.callCenterConfirmed.toLocaleString() }}</div>
            <div class="acc-lbl">✓ Confirmed</div>
            <div class="acc-sub">AI confirmed → status Confirmed</div>
          </div>
          <div class="acc-bucket b-green">
            <div class="acc-num">{{ aco.processed.toLocaleString() }}</div>
            <div class="acc-lbl">✓ Processed</div>
            <div class="acc-sub">cash collected</div>
          </div>
          <div class="acc-bucket b-blue">
            <div class="acc-num">{{ aco.confirmed.toLocaleString() }}</div>
            <div class="acc-lbl">In pipeline</div>
            <div class="acc-sub">not resolved yet</div>
          </div>
          <div class="acc-bucket b-red">
            <div class="acc-num">{{ aco.cancelled.toLocaleString() }}</div>
            <div class="acc-lbl">✕ Cancelled / Failed</div>
            <div class="acc-sub">refused or returned</div>
          </div>
          <div class="acc-bucket b-gray" v-if="aco.pendingOther">
            <div class="acc-num">{{ aco.pendingOther.toLocaleString() }}</div>
            <div class="acc-lbl">Pending</div>
            <div class="acc-sub">awaiting outcome</div>
          </div>
        </div>

        <!-- one precision line -->
        <div class="acc-precision" v-if="aco.precision != null">
          <span class="acc-prec-big ok-text">{{ aco.precision }}%</span>
          <span>precision — of the <b>{{ (aco.processed + aco.cancelled).toLocaleString() }}</b> orders that reached a final outcome, this share was <b>Processed</b>.</span>
        </div>
      </div>
    </div><!-- ═══════════ /SUMMARY TAB ═══════════ -->

    <!-- ═══════════ DETAILS TAB ═══════════ -->
    <div v-if="isAdmin === true && data && activeTab==='details'">
      <!-- Live-shadow scorecard — a young sample. One clear note, then the cards. -->
      <div class="scorecard-head">
        <h3>Live shadow — this app's own orders</h3>
        <span class="sc-note" v-if="sc.resolved != null">
          Young sample ({{ sc.resolved.toLocaleString() }} resolved) — rates read low.
          For the real number, use the <b>Real Quality</b> backtest ({{ (bt && bt.recommended && bt.recommended.precision) || '86' }}%) on the Summary tab.
        </span>
      </div>
      <div class="cards shadow-cards">
        <div class="card">
          <div class="big">{{ sc.aiPrecision != null ? sc.aiPrecision + '%' : '—' }}</div>
          <div class="lbl">AI precision</div>
          <div class="note">% of resolved auto-confirms that reached Processed</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.callsSaved }}</div>
          <div class="lbl">Calls saved</div>
          <div class="note">auto-confirmed orders resolved</div>
        </div>
        <div class="card warn">
          <div class="big">{{ sc.aiWrong }}</div>
          <div class="lbl">Mistakes</div>
          <div class="note">confirmed but cancelled / returned</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.baselineDeliverRate != null ? sc.baselineDeliverRate + '%' : '—' }}</div>
          <div class="lbl">Baseline</div>
          <div class="note">confirm-everyone delivery rate</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.missedGood }}</div>
          <div class="lbl">Missed good</div>
          <div class="note">sent to call but would've delivered</div>
        </div>
      </div>

      <!-- ═══ WHY AI-confirmed orders got cancelled ═══ -->
      <div class="section cancel-analysis" v-if="cb && cb.total > 0">
        <button type="button" class="acc-header" :class="{ open: showCancelWhy }" @click="showCancelWhy = !showCancelWhy">
          <span class="acc-chevron">›</span>
          <span class="acc-icon">✕</span>
          <span class="acc-title-wrap">
            <span class="acc-title">Why AI-confirmed orders got cancelled</span>
            <span class="acc-meta">{{ cb.total }} cancelled by the call-center · <b class="ok-text">{{ Math.round(100 * cb.unpreventable / cb.total) }}%</b> the AI couldn't predict</span>
          </span>
          <span class="acc-cta">{{ showCancelWhy ? 'Masquer' : 'Voir' }}</span>
        </button>
        <div v-if="showCancelWhy" class="acc-body">
          <p class="acc-body-intro">
            When the AI says “Confirm” but a human cancels, it's usually something decided <b>on the call</b> —
            not a signal the AI could see at order time. Only <b>“duplicate”</b> is potentially preventable.
          </p>
          <div class="cancel-bars">
            <div v-for="c in cb.categories" :key="c.key" class="cbar-row">
              <span class="cbar-label">
                <span class="cbar-dot" :class="c.preventable ? 'pv' : 'npv'"></span>{{ c.label }}
              </span>
              <span class="cbar-track">
                <span class="cbar-fill" :class="c.preventable ? 'pv' : 'npv'"
                      :style="{ width: Math.max(4, Math.round(100 * c.count / cb.total)) + '%' }"></span>
              </span>
              <span class="cbar-count">{{ c.count }} <span class="muted">({{ Math.round(100 * c.count / cb.total) }}%)</span></span>
            </div>
            <div v-if="cb.uncategorized" class="cbar-row">
              <span class="cbar-label"><span class="cbar-dot npv"></span>Other (free-text)</span>
              <span class="cbar-track"><span class="cbar-fill npv" :style="{ width: Math.max(4, Math.round(100 * cb.uncategorized / cb.total)) + '%' }"></span></span>
              <span class="cbar-count">{{ cb.uncategorized }} <span class="muted">({{ Math.round(100 * cb.uncategorized / cb.total) }}%)</span></span>
            </div>
          </div>
          <p class="cancel-takeaway">
            ✅ <b>{{ cb.unpreventable }}</b> of {{ cb.total }} ({{ Math.round(100 * cb.unpreventable / cb.total) }}%) were
            <b>not predictable</b> from order data — the AI was right to confirm; the customer/stock decided otherwise.
            <template v-if="cb.preventable > 0"><br>⚠️ Only <b>{{ cb.preventable }}</b> were duplicates (potentially catchable by the guard).</template>
          </p>
        </div>
      </div>

      <!-- THRESHOLD ANALYSIS (live shadow — collapsed by default) -->
      <div class="section">
        <button type="button" class="acc-header" :class="{ open: showLiveThresholds }" @click="showLiveThresholds = !showLiveThresholds">
          <span class="acc-chevron">›</span>
          <span class="acc-icon">🎚️</span>
          <span class="acc-title-wrap">
            <span class="acc-title">Pick your threshold (live)</span>
            <span class="acc-meta">based on this app's shadow decisions</span>
          </span>
          <span class="acc-cta">{{ showLiveThresholds ? 'Masquer' : 'Voir' }}</span>
        </button>
        <div v-if="showLiveThresholds" class="acc-body">
          <p class="acc-body-intro">On your real orders: at each confidence bar, how many you'd auto-confirm and how accurate. <span class="dim">(Fills in as orders complete delivery.)</span></p>

          <!-- Impact of the live ML cutoff vs the old 0.85 default -->
          <div class="thr-gain" v-if="data.gained && data.gained.count > 0">
            <span class="thr-gain-badge">+{{ data.gained.count.toLocaleString() }}</span>
            <span>orders auto-confirmed by lowering the cutoff <b>{{ data.gained.from }}% → {{ data.gained.to }}%</b>
              <template v-if="data.gained.precision != null"> — <b :class="data.gained.precision >= 70 ? 'ok-text' : 'warn-text'">{{ data.gained.precision }}%</b> of the resolved extras were delivered.</template>
            </span>
          </div>

          <table class="thr">
            <thead><tr><th>Confidence bar</th><th>Auto-confirm %</th><th>Orders</th><th>Precision</th></tr></thead>
            <tbody>
              <tr v-for="t in data.thresholds" :key="t.threshold" :class="{ 'thr-current': data.currentThreshold === t.threshold }">
                <td>≥ {{ t.threshold }}% <span v-if="data.currentThreshold === t.threshold" class="thr-live-badge">🔴 seuil actif</span></td>
                <td>{{ t.coverage }}%</td>
                <td>{{ t.count }}</td>
                <td :class="precClass(t.precision)">{{ t.precision != null ? t.precision + '%' : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TABLE -->
      <div class="section">
        <h2>Order-by-order</h2>

        <!-- ═══ All filters in one card ═══ -->
        <div class="filter-card">
          <!-- row 1: decision pills -->
          <div class="frow">
            <span class="frow-label">Decision</span>
            <div class="pills">
              <button v-for="f in ['all','AUTO_CONFIRM','MANUAL','delivered','failed']" :key="f"
                      :class="{active: filter===f}" @click="filter=f">{{ label(f) }}</button>
            </div>
          </div>

          <!-- row 2: verdict pills (AI vs final DELIVERY) -->
          <div class="frow">
            <span class="frow-label">Delivery</span>
            <div class="pills">
              <button v-for="v in verdictFilters" :key="v.key"
                      :class="['vpill', v.cls, { active: fVerdict === v.key }]"
                      @click="fVerdict = v.key">{{ v.label }}</button>
            </div>
          </div>

          <!-- row 2b: agreement with call-center status (fast, same-day) -->
          <div class="frow">
            <span class="frow-label">Confirm</span>
            <div class="pills">
              <button v-for="v in agreeFilters" :key="v.key"
                      :class="['vpill', v.cls, { active: fAgree === v.key }]"
                      @click="fAgree = v.key">{{ v.label }}</button>
            </div>
          </div>

          <!-- row 3: search + dropdowns + confidence -->
          <div class="frow wrap">
            <input class="xsearch" type="text" v-model="search"
                   placeholder="🔍 Order ID, tracking number, phone, customer or seller…" />
            <!-- custom Model dropdown -->
            <div class="dd" :class="{open: ddOpen==='model'}">
              <button class="dd-btn" @click="toggleDd('model')">
                <span>{{ modelLabel }}</span><span class="dd-caret">▾</span>
              </button>
              <div class="dd-menu" v-if="ddOpen==='model'">
                <div v-for="m in modelOptions" :key="m.value"
                     class="dd-item" :class="{sel: fModel===m.value}"
                     @click="fModel=m.value; ddOpen=null">
                  <span class="dd-dot" :style="{background:m.color}"></span>{{ m.label }}
                </div>
              </div>
            </div>

            <!-- custom Status dropdown -->
            <div class="dd" :class="{open: ddOpen==='status'}">
              <button class="dd-btn" @click="toggleDd('status')">
                <span>{{ fStatus || 'Any real status' }}</span><span class="dd-caret">▾</span>
              </button>
              <div class="dd-menu" v-if="ddOpen==='status'">
                <div class="dd-item" :class="{sel: fStatus===''}" @click="fStatus=''; ddOpen=null">Any real status</div>
                <div v-for="s in statusOptions" :key="s"
                     class="dd-item" :class="{sel: fStatus===s}" @click="fStatus=s; ddOpen=null">{{ s }}</div>
              </div>
            </div>
            <div class="conf-range">
              <span class="clbl">Confidence</span>
              <div class="conf-inputs">
                <input type="number" min="0" max="100" v-model.number="confMin" class="cnum" />
                <span class="conf-sep">to</span>
                <input type="number" min="0" max="100" v-model.number="confMax" class="cnum" />
                <span class="conf-pct">%</span>
              </div>
            </div>
          </div>

          <!-- row 4: date range -->
          <div class="frow wrap">
            <div class="quick-chips">
              <button v-for="q in quickRanges" :key="q.days"
                      :class="{on: activeQuick === q.days}"
                      @click="quickRange(q.days)">{{ q.label }}</button>
            </div>
            <div class="date-fields">
              <date-picker
                v-model="dateRange"
                range
                format="YYYY-MM-DD"
                value-type="format"
                placeholder="Select date range…"
                :disabled-date="disabledAfterToday"
                @change="onRangeChange"
                class="ac-datepicker" />
              <span v-if="loading" class="inline-loader"><span class="spinner"></span> loading…</span>
            </div>
          </div>

          <!-- footer: active summary + reset all -->
          <div class="fcard-foot">
            <span class="muted sm count-line">
              <b>{{ filtered.length.toLocaleString() }}</b>
              order<template v-if="filtered.length !== 1">s</template>
              <template v-if="hasClientFilter"> match your filters</template>
              <template v-else-if="serverTotal !== null"> in this date range</template>
              <template v-if="hasClientFilter && loadedCount"> <span class="dim">(of {{ loadedCount.toLocaleString() }} in this date/decision range)</span></template>
            </span>
            <span v-if="fromDate || toDate" class="date-active">📅 {{ fromDate || 'start' }} → {{ toDate || 'today' }}</span>
            <button class="reset-all" v-if="anyFilterActive" @click="resetAllFilters">✕ Reset all filters</button>
          </div>
          <div v-if="isTruncated" class="trunc-warn">
            ⚠️ {{ serverTotal.toLocaleString() }} orders match this date range, but only {{ loadedCount.toLocaleString() }}
            are loaded. Narrow the date range to see the rest.
          </div>
        </div>
        <div class="table-wrap rows-card">
          <div v-if="loading && data" class="table-overlay"><span class="spinner"></span> Updating…</div>
        <table class="rows">
          <thead>
            <tr>
              <th class="c-date">Date</th>
              <th class="c-order">Order</th>
              <th class="c-addr">Address</th>
              <th class="c-phone">Phone</th>
              <th class="c-seller">Seller</th>
              <th class="c-agent">Agent</th>
              <th class="c-ai">AI said</th>
              <th class="c-conf">Conf.</th>
              <th class="c-why">Why</th>
              <th class="c-track">Tracking</th>
              <th class="c-status">Real status</th>
              <th class="c-verdict">Verdict</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in paged" :key="r._id">
              <td class="c-date"><span class="cell-date">{{ fmtDate(r.createdAt) }}</span></td>
              <td class="c-order"><span class="order-ref">{{ r.orderRef || '—' }}</span><div class="muted sm cust-name">{{ r.customer }}</div></td>
              <td class="c-addr">
                <span v-if="hasRealAddress(r)" class="addr-ok" :title="r.address">
                  <span class="addr-txt">{{ r.address }}</span>
                </span>
                <span v-else class="addr-bad" :title="r.address || 'no address'">
                  <span class="addr-txt">{{ r.address || '—' }}<i class="addr-flag">NO ADDRESS</i></span>
                </span>
                <div v-if="r.city && r.city !== r.address" class="addr-city">{{ r.city }}</div>
              </td>
              <td class="c-phone"><span class="phone-num">{{ r.phone }}</span></td>
              <td class="c-seller">
                <span v-if="r.seller" class="seller-cell" :title="r.seller">
                  <span class="seller-dot">{{ r.seller.slice(0,1).toUpperCase() }}</span>
                  <span class="seller-name">{{ r.seller }}</span>
                </span>
                <span v-else class="agent-none">—</span>
              </td>
              <td class="c-agent">
                <span v-if="r.agent" class="agent-badge" :class="r.agentType === 'AICallCenter' ? 'agent-ai' : 'agent-human'" :title="r.agent">
                  {{ r.agentType === 'AICallCenter' ? '🤖' : '👤' }}<span>{{ r.agent }}</span>
                </span>
                <span v-else class="agent-none">—</span>
              </td>
              <td class="c-ai"><span :class="'pill ' + (aiSaysConfirm(r)?'ok':'warn')">{{ aiSaysConfirm(r)?'Confirm':'Call' }}</span></td>
              <td class="c-conf">
                <span class="conf-val" :class="confClass(r.confidence, r.model)">{{ r.confidence }}%</span>
                <span class="model-tag">{{ r.model }}</span>
              </td>
              <td class="why c-why">
                <!-- address gate would block: show that reason first (report mode too) -->
                <div v-if="r.gate && r.gate.wouldBlock" class="guard-reason">{{ whyText('no_deliverable_address') }}</div>
                <template v-else>
                  <div v-for="(p,i) in r.positives.slice(0,2)" :key="'p'+i" class="pos">✓ {{ p }}</div>
                  <div v-for="(n,i) in r.negatives.slice(0,2)" :key="'n'+i" class="neg">! {{ n }}</div>
                  <div v-if="!r.positives.length && !r.negatives.length" class="guard-reason">{{ whyText(r.reasons[0]) }}</div>
                </template>
              </td>
              <td class="c-track">
                <span v-if="r.trackingNumber" class="track-code" :title="r.trackingNumber">{{ r.trackingNumber }}</span>
                <span v-else class="track-none">—</span>
              </td>
              <td class="c-status">
                <span class="status-badge" :class="statusClass(r.realStatus)">{{ r.realStatus }}</span>
                <div class="muted sm ship-line" v-if="r.shipStatus">ship: {{ r.shipStatus }}</div>
                <div class="cancel-reason" v-if="r.cancelReason" :title="r.cancelReason">✕ {{ r.cancelReason }}</div>
              </td>
              <td class="c-verdict">
                <span v-if="r.outcome==='pending'" class="pill muted-pill">pending</span>
                <span v-else-if="verdict(r)==='right'" class="pill ok">✓ AI right</span>
                <span v-else class="pill bad">✗ AI wrong</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div><!-- /table-wrap -->
        <p v-if="!filtered.length && !loading" class="empty-rows">No orders match these filters.</p>

        <!-- PAGINATION BAR -->
        <div v-if="filtered.length" class="pagination">
          <div class="pg-info">
            Showing <b>{{ rangeStart }}–{{ rangeEnd }}</b> of <b>{{ filtered.length.toLocaleString() }}</b>
            <span v-if="serverTotal !== null && serverTotal > filtered.length" class="muted"> (of {{ serverTotal.toLocaleString() }} in range)</span>
          </div>
          <div class="pg-controls" v-if="totalPages > 1">
            <button class="pg-btn" :disabled="page === 1" @click="page = 1" title="First">«</button>
            <button class="pg-btn" :disabled="page === 1" @click="page--" title="Previous">‹</button>
            <button v-for="p in pageWindow" :key="p"
                    class="pg-num" :class="{ on: p === page }"
                    @click="page = p">{{ p }}</button>
            <button class="pg-btn" :disabled="page === totalPages" @click="page++" title="Next">›</button>
            <button class="pg-btn" :disabled="page === totalPages" @click="page = totalPages" title="Last">»</button>
          </div>
          <div class="pg-size">
            <span class="muted sm">Rows:</span>
            <button v-for="n in [10,25,50,100]" :key="n"
                    class="pg-size-btn" :class="{ on: perPage === n }"
                    @click="perPage = n">{{ n }}</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isAdmin === true && !loading && !data" class="loading">No data. Create some orders first (shadow mode logs them here).</div>
  </div>
</template>

<script>
const ADMIN_TYPES = ['Admin', 'Administrateur', 'CEO'];

export default {
  name: 'AutoConfirmQuality',
  data() {
    return {
      // Build marker — if the page shows an OLD value (or no stamp), the frontend
      // pipeline served a STALE dist/ and did not rebuild.
      buildStamp: 'build 2026-09-11 · v17 (readable guard reasons)',
      loading: false, data: null, bt: null, filter: 'all', isAdmin: null,
      whThreshold: null,   // configured aiCallCenter.threshold for the header-selected warehouse
      whRequireAddress: null, // configured aiCallCenter.requireAddress (address gate on/off)
      whLabel: '',         // that warehouse's country/name label
      page: 1, perPage: 25, fromDate: '', toDate: '', dateRange: [], showLiveThresholds: false, showRealQuality: true, showCancelWhy: false,
      activeTab: 'summary', showShadowNote: false,
      search: '', fModel: '', fStatus: '', confMin: 0, confMax: 100, fVerdict: 'all',
      ddOpen: null,
      modelOptions: [
        { value: '', label: 'All models', color: '#9ca3af' },
        { value: 'ml', label: 'AI model (ml)', color: '#2563eb' },
        { value: 'rule', label: 'Rule', color: '#f59e0b' },
        { value: 'guard', label: 'Guard (junk/duplicate)', color: '#dc2626' },
      ],
      verdictFilters: [
        { key: 'all', label: 'All', cls: '' },
        { key: 'right', label: '✓ AI right (delivery)', cls: 'v-right' },
        { key: 'wrong', label: '✗ AI wrong (delivery)', cls: 'v-wrong' },
        { key: 'confirm_delivered', label: 'Confirm → Delivered (Processed)', cls: 'v-right' },
        { key: 'confirm_failed', label: 'Confirm → Failed', cls: 'v-wrong' },
        { key: 'call_failed', label: 'Call → Failed', cls: 'v-right' },
        { key: 'call_delivered', label: 'Call → Delivered (Processed)', cls: 'v-wrong' },
        { key: 'pending', label: 'Not resolved yet', cls: 'v-pending' },
      ],
      // agreement vs the call-center's own status (fast signal, same-day)
      agreeFilters: [
        { key: 'all', label: 'All', cls: '' },
        { key: 'confirm_confirmed', label: 'AI Confirm = Confirmed ✓', cls: 'v-right' },
        { key: 'confirm_cancelled', label: 'AI Confirm but Cancelled', cls: 'v-wrong' },
        // matches the accuracy-panel red bucket exactly: ALL failure outcomes
        // (Cancelled + refused/returned/out-of-stock/…), not just literal "Cancelled".
        { key: 'confirm_failed_all', label: 'Confirm → Cancelled / Failed (all)', cls: 'v-wrong' },
        { key: 'call_confirmed', label: 'AI Call but Confirmed', cls: 'v-pending' },
        { key: 'call_cancelled', label: 'AI Call = Cancelled ✓', cls: 'v-right' },
      ],
      fAgree: 'all',
      activeQuick: null,
      quickRanges: [
        { label: 'Today', days: 0 },
        { label: 'Yesterday', days: 1 },
        { label: 'Last 7 days', days: 7 },
        { label: 'Last 30 days', days: 30 },
        { label: 'Last 90 days', days: 90 },
      ],
    };
  },
  computed: {
    // the warehouse country currently selected in the app header (global selector)
    warhouseSelected() {
      try { return this.$store.getters["wharhouse/warhouseSelected"]({ user: {} }); }
      catch (e) { return null; }
    },
    sc() { return (this.data && this.data.scorecard) || {}; },
    aco() { return (this.data && this.data.scorecard && this.data.scorecard.acOutcome) || null; },
    cb() { return (this.data && this.data.cancelBreakdown) || null; },
    statusOptions() {
      if (!this.data) return [];
      return [...new Set(this.data.rows.map(r => r.realStatus).filter(Boolean))].sort();
    },
    anyXFilter() {
      return this.search || this.fModel || this.fStatus || this.confMin > 0 || this.confMax < 100;
    },
    anyFilterActive() {
      return this.anyXFilter || this.filter !== 'all' || this.fVerdict !== 'all' || this.fAgree !== 'all' || this.fromDate || this.toDate;
    },
    filtered() {
      if (!this.data) return [];
      const f = this.filter;
      const q = (this.search || '').toLowerCase().trim();
      return this.data.rows.filter(r => {
        // decision / outcome pill
        if (f === 'AUTO_CONFIRM' || f === 'MANUAL') { if (r.decision !== f) return false; }
        else if (f === 'delivered' || f === 'failed') { if (r.outcome !== f) return false; }
        // model
        if (this.fModel && r.model !== this.fModel) return false;
        // real status
        if (this.fStatus && r.realStatus !== this.fStatus) return false;
        // confidence range — coerce empty/NaN inputs to safe 0/100 bounds so
        // clearing a box doesn't silently invert the filter to zero results.
        const c = r.confidence || 0;
        const lo = Number.isFinite(+this.confMin) ? +this.confMin : 0;
        const hi = Number.isFinite(+this.confMax) && this.confMax !== '' ? +this.confMax : 100;
        if (c < lo || c > hi) return false;
        // free-text search — order ref, tracking number, phone, customer, seller
        if (q) {
          const hay = `${r.orderRef || ''} ${r.trackingNumber || ''} ${r.phone || ''} ${r.customer || ''} ${r.seller || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        // verdict (AI-said × real-result combinations)
        if (this.fVerdict !== 'all' && !this.matchVerdict(r, this.fVerdict)) return false;
        // agreement with call-center status
        if (this.fAgree !== 'all' && !this.matchAgree(r, this.fAgree)) return false;
        return true;
      });
    },
    totalPages() { return Math.max(1, Math.ceil(this.filtered.length / this.perPage)); },
    paged() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    rangeStart() { return this.filtered.length ? (this.page - 1) * this.perPage + 1 : 0; },
    rangeEnd() { return Math.min(this.page * this.perPage, this.filtered.length); },
    // a window of up to 5 page numbers centered on the current page
    pageWindow() {
      const total = this.totalPages, cur = this.page, span = 5;
      let start = Math.max(1, cur - Math.floor(span / 2));
      let end = Math.min(total, start + span - 1);
      start = Math.max(1, end - span + 1);
      const out = [];
      for (let p = start; p <= end; p++) out.push(p);
      return out;
    },
    todayStr() { return new Date().toISOString().slice(0, 10); },
    modelLabel() { const m = this.modelOptions.find(o => o.value === this.fModel); return m ? m.label : 'All models'; },
    // server returned fewer rows than the true total for this date+decision+model
    // query → some matching orders aren't loaded for client-side refinement.
    serverTotal() { return this.data && typeof this.data.total === 'number' ? this.data.total : null; },
    loadedCount() { return this.data ? (this.data.rows || []).length : 0; },
    // client-only refinements active? (these narrow the loaded set further —
    // decision/model/date are server-side and already reflected in loadedCount)
    hasClientFilter() {
      return this.fVerdict !== 'all' || this.fAgree !== 'all' || !!this.fStatus ||
        !!this.search || this.confMin > 0 || this.confMax < 100 ||
        this.filter === 'delivered' || this.filter === 'failed';
    },
    isTruncated() {
      // real truncation = the server had MORE matches than it could send back
      return this.serverTotal !== null && this.serverTotal > this.loadedCount;
    },
  },
  watch: {
    // header warehouse changed → reload the WHOLE page (scorecard + table + banner)
    // filtered to that warehouse. load() sets this.loading (spinner shows).
    warhouseSelected() { this.page = 1; this.load(); },
    // filter (decision) + model are SERVER-side → reload full dataset.
    filter() { this.page = 1; this.load(); },
    fModel() { this.page = 1; this.load(); },
    // the rest are client-side refinements → just reset the page.
    perPage() { this.page = 1; },
    search() { this.page = 1; },
    fStatus() { this.page = 1; },
    confMin() { this.page = 1; },
    confMax() { this.page = 1; },
    fVerdict() { this.page = 1; },
    fAgree() { this.page = 1; },
  },
  methods: {
    async checkAdmin() {
      try {
        const me = await this.$server.me('users');
        const user = me && me.content ? me.content : me;
        this.isAdmin = !!(user && user.type && ADMIN_TYPES.indexOf(user.type) !== -1);
      } catch (e) {
        this.isAdmin = false;
      }
      return this.isAdmin;
    },
    async load() {
      this.loading = true;
      // real-data backtest (the true quality) + live shadow logs, in parallel
      try {
        const btRaw = await this.$server.action('autoconfirm', 'backtest', {});
        const bt = btRaw && btRaw.content ? btRaw.content : btRaw;
        this.bt = bt && bt.thresholds ? bt : null;
      } catch (e) { this.bt = null; }
      try {
        // Push the coarse filters (date, decision, model) to the SERVER so they
        // filter the whole dataset — otherwise "Last 90 days" only sees the newest
        // page. Verdict/agree/status/confidence/search still refine client-side.
        const payload = { limit: 5000 };
        if (this.fromDate) payload.from = this.fromDate;
        if (this.toDate) payload.to = this.toDate;
        if (this.filter === 'AUTO_CONFIRM' || this.filter === 'MANUAL') payload.decision = this.filter;
        if (this.fModel) payload.model = this.fModel;
        // Filter the WHOLE page by the header-selected warehouse (scorecard + table).
        const _country = await this.warhouseSelected;
        if (_country) payload.country = _country;
        const raw = await this.$server.action('autoconfirm', 'quality', payload);
        this.data = raw && raw.content ? raw.content : raw;
        this.page = 1;
      } catch (e) {
        console.log('quality load error', e && e.message);
      }
      await this.loadWhThreshold();
      this.loading = false;
    },
    // Read the configured aiCallCenter.threshold for the header-selected warehouse,
    // so the quality page shows/highlights YOUR setting (per-warehouse, per-project).
    async loadWhThreshold() {
      try {
        // warhouseSelected is an ASYNC getter (returns a Promise) — must await it,
        // else `country` is a Promise and never matches, so the banner never shows.
        const country = await this.warhouseSelected;
        if (!country) { this.whThreshold = null; this.whLabel = ''; return; }
        const s = await this.$server.get('settings');
        const details = (s && s.content && s.content.details) || [];
        const d = details.find(x => x && x.country === country);
        const cfg = d && d.aiCallCenter;
        this.whThreshold = (cfg && typeof cfg.threshold === 'number') ? cfg.threshold : null;
        // address gate setting for this warehouse (same config object, no extra call)
        this.whRequireAddress = cfg ? (cfg.requireAddress === true) : null;
        this.whLabel = country;
      } catch (e) { this.whThreshold = null; this.whRequireAddress = null; this.whLabel = ''; }
    },
    // Highlight the sweep row nearest to the configured threshold (rows are 70/75/80/...).
    isWhThreshold(rowThr) {
      if (this.whThreshold == null) return false;
      const target = Math.round(this.whThreshold * 100);
      // match the closest sweep row to the configured value
      const rows = (this.bt && this.bt.thresholds) ? this.bt.thresholds.map(t => t.threshold) : [];
      if (!rows.length) return rowThr === target;
      const closest = rows.reduce((a, b) => Math.abs(b - target) < Math.abs(a - target) ? b : a);
      return rowThr === closest;
    },
    toggleDd(which) { this.ddOpen = this.ddOpen === which ? null : which; },
    resetXFilters() { this.search = ''; this.fModel = ''; this.fStatus = ''; this.confMin = 0; this.confMax = 100; },
    resetAllFilters() {
      this.resetXFilters();
      this.filter = 'all';
      this.fVerdict = 'all';
      this.fAgree = 'all';
      this.fromDate = ''; this.toDate = ''; this.dateRange = []; this.activeQuick = null;
      this.load();
    },
    clearDates() { this.fromDate = ''; this.toDate = ''; this.dateRange = []; this.activeQuick = null; this.load(); },
    disabledAfterToday(date) { return date > new Date(); },
    // Format a Date as a LOCAL calendar day (YYYY-MM-DD) — never toISOString(),
    // which converts to UTC and can shift the day for UTC-positive timezones.
    localDay(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    // The picker emits YYYY-MM-DD STRINGS (value-type="format") — no TZ shift.
    onRangeChange(val) {
      if (Array.isArray(val) && val[0] && val[1]) {
        this.fromDate = val[0]; this.toDate = val[1];
      } else { this.fromDate = ''; this.toDate = ''; }
      this.activeQuick = null;
      this.load();
    },
    quickRange(days) {
      let from, to;
      if (days === 1) { // Yesterday = that single day
        const y = new Date(); y.setDate(y.getDate() - 1); from = y; to = y;
      } else {
        to = new Date();
        from = new Date(); from.setDate(from.getDate() - days);
      }
      const fs = this.localDay(from), ts = this.localDay(to);
      this.dateRange = [fs, ts];   // strings, matching value-type="format"
      this.fromDate = fs;
      this.toDate = ts;
      this.activeQuick = days;
      this.load();
    },
    fmtDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    verdict(r) {
      const good = r.decision === 'AUTO_CONFIRM';
      // AI "right" = it confirmed and it delivered, OR it said call and it failed
      if (good) return r.outcome === 'delivered' ? 'right' : 'wrong';
      return r.outcome === 'failed' ? 'right' : 'wrong';
    },
    // Match a row to one of the verdict / combination filters.
    matchVerdict(r, key) {
      const confirm = r.decision === 'AUTO_CONFIRM';
      const oc = r.outcome; // delivered | failed | pending
      if (key === 'pending') return oc === 'pending';
      if (oc === 'pending') return false; // not-yet-tested can't match a verdict
      const v = this.verdict(r); // 'right' | 'wrong'
      switch (key) {
        case 'right': return v === 'right';
        case 'wrong': return v === 'wrong';
        case 'confirm_delivered': return confirm && oc === 'delivered';
        case 'confirm_failed': return confirm && oc === 'failed';
        case 'call_failed': return !confirm && oc === 'failed';
        case 'call_delivered': return !confirm && oc === 'delivered';
        default: return true;
      }
    },
    // Match a row against agreement with the call-center's own status
    // (Confirmed / Cancelled). Fast same-day signal, unlike final delivery.
    matchAgree(r, key) {
      // Guard rows (junk phone / duplicate) are pre-scoring safety rejects, not
      // genuine AI "calls" — exclude them from agreement stats (matches the
      // backend scorecard which also excludes model==='guard').
      if (r.model === 'guard') return false;
      const confirm = r.decision === 'AUTO_CONFIRM';
      const st = r.realStatus;
      switch (key) {
        case 'confirm_confirmed': return confirm && st === 'Confirmed';
        case 'confirm_cancelled': return confirm && st === 'Cancelled';
        // ALL failure outcomes for an auto-confirm — matches the panel's red
        // "Cancelled / Failed" bucket (outcome === 'failed'), which is broader
        // than the literal-status "Cancelled" chip above.
        case 'confirm_failed_all': return confirm && r.outcome === 'failed';
        case 'call_confirmed': return !confirm && st === 'Confirmed';
        case 'call_cancelled': return !confirm && st === 'Cancelled';
        default: return true;
      }
    },
    precClass(p) { return p == null ? '' : (p >= 85 ? 'good' : p >= 70 ? 'mid' : 'low'); },
    // color-code the per-order confidence (>=threshold green, near amber, low red).
    // guard/rule rows (no real ml score) stay neutral.
    confClass(p, model) {
      if (model !== 'ml' || p == null) return 'neutral';
      return p >= 85 ? 'good' : p >= 70 ? 'mid' : 'low';
    },
    // color-code the real order status
    statusClass(st) {
      const s = (st || '').toLowerCase();
      if (['confirmed', 'delivered', 'paid', 'processed', 'received'].includes(s)) return 'st-green';
      if (['cancelled', 'refused', 'return', 'spam', 'double', 'rembourser', 'refunded', 'outofstock'].includes(s)) return 'st-red';
      if (['pending', 'unreached', 'toremind'].includes(s)) return 'st-gray';
      if (['shipped', 'a transférer', 'awaiting preparation', 'prepared', 'to prepare'].includes(s)) return 'st-blue';
      return 'st-gray';
    },
    label(f) {
      return { all: 'All', AUTO_CONFIRM: 'Auto-confirmed', MANUAL: 'Sent to call', delivered: 'Delivered (Processed)', failed: 'Failed' }[f] || f;
    },
    // Turn a raw reason CODE (from guard/rule rows that have no rich SHAP text)
    // into a clear human sentence, so the "Why" column never shows raw slugs.
    // Does the AI end up CONFIRMING this order? "Confirm" only when the model said
    // AUTO_CONFIRM AND the address gate would not block it (no usable address + none
    // on file). Otherwise it's a "Call" — this mirrors what happens live, so the
    // shadow page shows the real outcome even in report mode.
    // Does this row have a usable delivery address? Prefer the gate's own verdict
    // (computed server-side with the shared rules); fall back to a light check for
    // rows logged before the gate shipped.
    hasRealAddress(r) {
      if (r.gate && typeof r.gate.wouldBlock === 'boolean') return !r.gate.wouldBlock;
      const a = (r.address || '').trim();
      return a.length >= 8 && !/^[-–—._,/\\\s|]+$/.test(a);
    },
    aiSaysConfirm(r) {
      if (r.decision !== 'AUTO_CONFIRM') return false;
      if (r.gate && r.gate.wouldBlock) return false; // address gate -> Call
      return true;
    },
    whyText(raw) {
      const s = String(raw || '');
      if (/invalid_or_junk_phone/.test(s)) return '🛡️ Sent to call — phone number is invalid or missing.';
      if (/no_deliverable_address/.test(s)) return '🛡️ Sent to call — no usable delivery address and none on file for this phone.';
      if (/possible_duplicate_order_today/.test(s)) return '🛡️ Sent to call — looks like a duplicate order placed today.';
      if (/^no_history/.test(s)) return 'New customer — no delivery history yet, so a human checks it.';
      if (/^too_few_orders/.test(s)) {
        const m = s.match(/\((\d+)\s*<\s*(\d+)\)/);
        return m ? `Too few past orders (${m[1]}, need ${m[2]}) — sent to call.` : 'Too few past orders — sent to call.';
      }
      if (/^rate_below_threshold/.test(s)) {
        const m = s.match(/\((\d+)%/);
        return m ? `Weak delivery history (${m[1]}%) — sent to call.` : 'Weak delivery history — sent to call.';
      }
      if (/^trusted:/.test(s)) return s.replace(/^trusted:\s*/, '✓ Trusted customer: ');
      return s; // fallback: show whatever it is rather than nothing
    },
  },
  async mounted() {
    // close custom dropdowns when clicking outside them
    this._closeDd = (e) => { if (!e.target.closest('.dd')) this.ddOpen = null; };
    document.addEventListener('click', this._closeDd);
    const ok = await this.checkAdmin();
    if (ok) this.load();
    else window.location.href = '/'; // non-admins: silent redirect to dashboard
  },
  beforeDestroy() {
    if (this._closeDd) document.removeEventListener('click', this._closeDd);
  },
};
</script>

<style scoped>
.ac-quality { padding: 24px 32px; width: 100%; color: #1f2937; box-sizing: border-box; }
.ac-head h1 { font-size: 22px; font-weight: 700; margin: 0; }
.build-stamp { font-size: 11px; font-weight: 600; color: #6b7280; background: #eef2f7; border: 1px solid #dbe2ea; padding: 2px 8px; border-radius: 20px; vertical-align: middle; margin-left: 8px; }
.ac-head .sub { color: #6b7280; font-size: 13px; margin: 4px 0 12px; }
.refresh { background: #2563eb; color: #fff; border: 0; padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; }
.refresh:disabled { opacity: .6; }
.loading { padding: 40px; text-align: center; color: #6b7280; }
/* ── tabs ── */
.ac-tabs { display: flex; gap: 6px; border-bottom: 2px solid #e5e7eb; margin: 4px 0 20px; }
.ac-tab { background: none; border: 0; border-bottom: 2px solid transparent; margin-bottom: -2px;
  padding: 9px 16px; font-size: 14px; font-weight: 600; color: #6b7280; cursor: pointer; }
.ac-tab:hover { color: #374151; }
.ac-tab.on { color: #2563eb; border-bottom-color: #2563eb; }
/* ── inline explain toggle ── */
.acc-explain { margin-top: 12px; }
.link-toggle { background: none; border: 0; color: #2563eb; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0; }
.link-toggle:hover { text-decoration: underline; }
/* spinner + loading overlays */
.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid #dbeafe; border-top-color: #2563eb; border-radius: 50%; animation: spin .7s linear infinite; vertical-align: -2px; }
.spinner.big { width: 22px; height: 22px; border-width: 3px; }

/* Reload indicator on warehouse switch */
.reload-bar { display: flex; align-items: center; gap: 10px; margin: 4px 0 16px; padding: 11px 16px;
  background: linear-gradient(120deg, #eff6ff, #f0f9ff); border: 1px solid #bfdbfe; border-radius: 12px;
  font-size: 13.5px; color: #1e40af; font-weight: 500; }
.reload-bar b { font-weight: 700; }
.fade-enter-active, .fade-leave-active { transition: opacity .2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
@keyframes spin { to { transform: rotate(360deg); } }
.inline-loader { color: #2563eb; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.table-wrap { position: relative; min-height: 60px; }
.table-overlay { position: absolute; inset: 0; background: rgba(255,255,255,.72); backdrop-filter: blur(1.5px); display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 10; color: #2563eb; font-size: 14px; font-weight: 500; border-radius: 14px; }
.denied { padding: 60px; text-align: center; color: #b45309; font-size: 16px; font-weight: 600; background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; margin-top: 20px; }
.realbox { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 14px; padding: 18px; margin: 14px 0; }
.realbox.muted-box { background: #f9fafb; color: #6b7280; text-align: center; }
.realhead h2 { font-size: 18px; font-weight: 700; margin: 0; }
.cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 14px; }
.cols h3 { font-size: 14px; font-weight: 700; margin: 0 0 6px; }
.divide { border: 0; border-top: 1px dashed #d1d5db; margin: 28px 0 16px; }
.shadow-title { font-size: 16px; font-weight: 700; }
.collapsible { cursor: pointer; user-select: none; }
.collapsible .caret { display: inline-block; width: 14px; color: #6b7280; font-size: 12px; }
.collapsible:hover { color: #2563eb; }

/* Accordion header — proper clickable card (replaces flat text links) */
.acc-header {
  width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 13px 16px;
  cursor: pointer; transition: border-color .16s, box-shadow .16s, background .16s;
  font-family: inherit;
}
.acc-header:hover { border-color: #bfdbfe; box-shadow: 0 2px 10px -4px rgba(37,99,235,.18); }
.acc-header.open { background: #f8fafc; border-color: #dbeafe; }
.acc-chevron {
  flex-shrink: 0; width: 22px; height: 22px; border-radius: 7px; display: flex; align-items: center; justify-content: center;
  background: #eff6ff; color: #2563eb; font-size: 17px; font-weight: 700; line-height: 1;
  transition: transform .2s ease;
}
.acc-header.open .acc-chevron { transform: rotate(90deg); }
.acc-icon { flex-shrink: 0; font-size: 17px; line-height: 1; }
.acc-title-wrap { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.acc-title { font-size: 14.5px; font-weight: 700; color: #1e293b; letter-spacing: -.01em; }
.acc-meta { font-size: 12px; color: #64748b; }
.acc-meta b { color: #16a34a; }
.acc-cta {
  flex-shrink: 0; font-size: 12px; font-weight: 700; color: #2563eb; background: #eff6ff;
  border: 1px solid #dbeafe; border-radius: 20px; padding: 4px 12px; white-space: nowrap;
}
.acc-header:hover .acc-cta { background: #2563eb; color: #fff; border-color: #2563eb; }

/* Expanded accordion body — white card, connected to the header above */
.acc-body {
  background: #fff; border: 1px solid #e5e7eb; border-top: none;
  border-radius: 0 0 12px 12px; padding: 18px 20px 20px; margin-top: -1px;
  box-shadow: 0 4px 16px -10px rgba(15,23,42,.12);
}
.acc-body-intro { font-size: 13px; color: #475569; line-height: 1.6; margin: 0 0 16px; }
.acc-body-intro b { color: #1e293b; }
.acc-body-intro .dim { color: #94a3b8; }
/* when a body is open, square off the header's bottom so they read as one card */
.acc-header.open { border-radius: 12px 12px 0 0; }
@media (max-width: 820px) { .cols { grid-template-columns: 1fr; } }
/* ── Auto-Confirm accuracy panel ── */
.acc-panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 18px 20px; margin: 8px 0 20px; box-shadow: 0 1px 3px rgba(16,24,40,.04); }
.acc-head h3 { margin: 0 0 2px; font-size: 17px; font-weight: 700; }
.acc-head .sub { margin: 0 0 14px; }
.acc-buckets { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.acc-bucket { border-radius: 12px; padding: 14px 16px; border: 1px solid; }
.acc-bucket .acc-num { font-size: 30px; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.acc-bucket .acc-lbl { font-size: 13px; font-weight: 700; margin-top: 6px; }
.acc-bucket .acc-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
.acc-bucket.b-green { background: #f0fdf4; border-color: #bbf7d0; }
.acc-bucket.b-green .acc-num { color: #16a34a; }
.acc-bucket.b-blue  { background: #eff6ff; border-color: #bfdbfe; }
.acc-bucket.b-blue .acc-num { color: #2563eb; }
.acc-bucket.b-red   { background: #fef2f2; border-color: #fecaca; }
.acc-bucket.b-red .acc-num { color: #dc2626; }
.acc-bucket.b-gray  { background: #f9fafb; border-color: #e5e7eb; }
.acc-bucket.b-gray .acc-num { color: #6b7280; }
.acc-bucket.b-teal  { background: #f0fdfa; border-color: #99f6e4; }
.acc-bucket.b-teal .acc-num { color: #0d9488; }
.acc-precision { margin: 14px 0 0; display: flex; align-items: center; gap: 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; padding: 12px 16px; font-size: 13px; color: #334155; line-height: 1.5; }
.acc-prec-big { font-size: 28px; font-weight: 800; line-height: 1; flex-shrink: 0; }
.acc-note { margin: 8px 0 0; color: #6b7280; font-size: 12px; line-height: 1.5; }
.acc-agree { margin: 4px 0 0; }
.acc-agree .acc-bucket.wide { width: 100%; box-sizing: border-box; }
.acc-buckets-head { margin: 14px 0 6px; font-size: 12.5px; font-weight: 600; color: #64748b; }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin: 14px 0; }
.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
.card.warn { border-color: #fecaca; }
.card .big { font-size: 28px; font-weight: 800; color: #16a34a; }
.card.warn .big { color: #dc2626; }
.card .lbl { font-size: 13px; font-weight: 600; margin-top: 2px; }
.card .note { font-size: 11px; color: #9ca3af; margin-top: 2px; }
/* live-shadow scorecard: honest "young sample" caption + subdued cards so the
   backtest above reads as the headline number, not these censored live rates. */
.scorecard-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; margin: 16px 0 12px; }
.scorecard-head h3 { font-size: 15px; font-weight: 700; color: #1e293b; margin: 0; }
.sc-note { font-size: 12px; color: #94a3b8; line-height: 1.5; }
.sc-note b { color: #64748b; font-weight: 600; }
.shadow-cards .card { background: #fafafa; box-shadow: none; }
.shadow-cards .card .big { color: #4b5563; }          /* neutral, not celebratory green */
.shadow-cards .card.warn .big { color: #b45309; }     /* amber, not alarming red */
.section { margin-top: 24px; }

/* ── cancel-reason breakdown ── */
.ok-text { color: #16a34a; }
.cancel-bars { display: flex; flex-direction: column; gap: 8px; margin: 14px 0; max-width: 720px; }
.cbar-row { display: grid; grid-template-columns: 260px 1fr 92px; align-items: center; gap: 12px; font-size: 13px; }
@media (max-width: 640px) { .cbar-row { grid-template-columns: 1fr; gap: 3px; } }
.cbar-label { color: #374151; display: flex; align-items: center; gap: 7px; }
.cbar-dot { flex: none; width: 9px; height: 9px; border-radius: 50%; }
.cbar-dot.npv { background: #94a3b8; }   /* not preventable — grey */
.cbar-dot.pv  { background: #f59e0b; }   /* preventable — amber */
.cbar-track { background: #f1f3f5; border-radius: 6px; height: 16px; overflow: hidden; }
.cbar-fill { display: block; height: 100%; border-radius: 6px; }
.cbar-fill.npv { background: #cbd5e1; }
.cbar-fill.pv  { background: #f59e0b; }
.cbar-count { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; color: #1f2937; }
.cancel-takeaway { margin-top: 16px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 12px 16px; font-size: 13px; line-height: 1.6; color: #334155; }
.cancel-takeaway b { color: #15803d; }

/* threshold table inside the white body */
.acc-body .thr { width: 100%; border-collapse: collapse; font-size: 13px; }
.acc-body .thr th { text-align: left; color: #94a3b8; font-weight: 700; font-size: 10.5px; text-transform: uppercase; letter-spacing: .05em; padding: 9px 12px; border-bottom: 1px solid #eef2f6; background: #fafbfc; }
.acc-body .thr th:not(:first-child) { text-align: right; }
.acc-body .thr td { padding: 11px 12px; border-bottom: 1px solid #f4f6f9; font-variant-numeric: tabular-nums; color: #334155; }
.acc-body .thr td:not(:first-child) { text-align: right; }
.acc-body .thr td:first-child { font-weight: 600; color: #1e293b; }
.acc-body .thr tbody tr:last-child td { border-bottom: 0; }
.acc-body .thr tbody tr:hover td { background: #f8fafc; }
.acc-body .thr { border: 1px solid #eef2f6; border-radius: 10px; overflow: hidden; }

/* live-cutoff impact banner + highlighted current-threshold row */
.thr-gain { display: flex; align-items: center; gap: 12px; margin: 0 0 14px; padding: 11px 15px;
  background: linear-gradient(120deg, #ecfdf5, #f0fdf4); border: 1px solid #a7f3d0; border-radius: 11px;
  font-size: 13px; color: #334155; line-height: 1.5; }
.thr-gain-badge { flex-shrink: 0; font-size: 15px; font-weight: 800; color: #fff; background: #059669;
  border-radius: 8px; padding: 4px 11px; letter-spacing: -.01em; }
.thr-gain b { color: #15803d; }
.warn-text { color: #b45309; font-weight: 700; }
.acc-body .thr tbody tr.thr-current td { background: #fef2f2; font-weight: 700; }
.acc-body .thr tbody tr.thr-current td:first-child { box-shadow: inset 3px 0 0 #dc2626; }
.thr-live-badge { font-size: 10px; font-weight: 700; color: #dc2626; background: #fee2e2; border: 1px solid #fecaca;
  border-radius: 20px; padding: 1px 8px; margin-left: 6px; white-space: nowrap; }
.section h2 { font-size: 16px; font-weight: 700; }
.section .sub { color: #6b7280; font-size: 12px; margin: 2px 0 8px; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th { text-align: left; color: #6b7280; font-weight: 600; font-size: 11px; text-transform: uppercase; padding: 6px 8px; border-bottom: 2px solid #e5e7eb; }
td { padding: 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.thr td.good { color: #16a34a; font-weight: 700; }
.thr td.mid { color: #f59e0b; font-weight: 700; }
.thr td.low { color: #dc2626; }

/* ═══════════ Order table — polished design ═══════════ */
.rows-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16,24,40,.04), 0 4px 16px -8px rgba(16,24,40,.10);
  /* scroll BOTH ways: sideways for the 12 columns, and vertically so a long
     page of rows stays in a fixed frame with the header pinned on top. */
  overflow: auto;
  max-height: 72vh;
  overscroll-behavior: contain;
}
/* a visible, easy-to-grab scrollbar (the default one is nearly invisible here) */
.rows-card::-webkit-scrollbar { width: 10px; height: 10px; }
.rows-card::-webkit-scrollbar-track { background: #f8fafc; }
.rows-card::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; border: 2px solid #f8fafc; }
.rows-card::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
/* 12 columns now (address / seller / agent / tracking) — give them room so
   cells never get crushed; .rows-card scrolls both ways. */
table.rows { font-size: 13px; min-width: 1530px; }

/* header — sticky, soft gradient, clear separation */
table.rows thead th {
  position: sticky; top: 0; z-index: 2;
  background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
  color: #6b7280;
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: 11px 14px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

/* body cells — roomier, aligned, subtle dividers */
table.rows tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: top;
  color: #1f2937;
}
table.rows tbody tr:last-child td { border-bottom: 0; }

/* zebra + hover */
table.rows tbody tr:nth-child(even) { background: #fcfcfd; }
table.rows tbody tr { transition: background .12s ease; }
table.rows tbody tr:hover { background: #f5f8ff; }

/* ── per-column treatment ── */
.c-date   { width: 118px; white-space: nowrap; }
.c-order  { width: 210px; }
.c-phone  { width: 140px; white-space: nowrap; }
/* ── address / seller / agent columns ── */
.c-addr   { width: 210px; }
.c-seller { width: 150px; }
.c-agent  { width: 128px; }

/* address: a soft chip, green when deliverable, amber when not */
.addr-ok, .addr-bad {
  display: inline-flex; align-items: flex-start; gap: 5px;
  padding: 3px 8px 3px 7px; border-radius: 7px;
  font-size: 12.5px; line-height: 1.35; max-width: 100%;
}
.addr-ok  { background: #f0fdf4; border: 1px solid #dcfce7; color: #166534; }
.addr-bad { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
.addr-ok::before  { content: "📍"; font-size: 11px; line-height: 1.45; flex: none; }
.addr-bad::before { content: "⚠"; font-size: 11px; line-height: 1.45; flex: none; }
.addr-txt { overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.addr-flag { display: block; font-size: 10.5px; font-weight: 700; letter-spacing: .02em; opacity: .85; margin-top: 1px; }
.addr-city { margin-top: 3px; color: #9ca3af; font-size: 11.5px; }

/* seller: avatar + name, mirrors the By-seller table */
.seller-cell { display: inline-flex; align-items: center; gap: 7px; max-width: 100%; }
.seller-dot {
  flex: none; width: 22px; height: 22px; border-radius: 6px;
  background: #eef2ff; color: #4338ca; border: 1px solid #e0e7ff;
  font-size: 11px; font-weight: 800; line-height: 20px; text-align: center;
}
.seller-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #334155; font-weight: 600; font-size: 12.5px; }

/* agent: a badge — violet for the AI, teal for a human */
.agent-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 20px;
  font-size: 11.5px; font-weight: 700; white-space: nowrap; max-width: 100%;
}
.agent-badge span { overflow: hidden; text-overflow: ellipsis; }
.agent-ai    { background: #f5f3ff; border: 1px solid #ddd6fe; color: #6d28d9; }
.agent-human { background: #f0fdfa; border: 1px solid #99f6e4; color: #0f766e; }
.agent-none  { color: #cbd5e1; }
/* tracking number — monospace so the codes line up and are easy to compare */
.c-track  { width: 150px; }
.track-code {
  display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11.5px; color: #334155;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 2px 7px;
}
.track-none { color: #cbd5e1; }
.c-ai     { width: 92px; }
.c-conf   { width: 92px; }
.c-why    { min-width: 300px; }
.c-status { width: 130px; }
/* stack the status badge, ship line and cancel-reason pill vertically (one under
   the other) instead of letting the inline-block pills sit side by side */
td.c-status { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
.c-verdict{ width: 110px; white-space: nowrap; }

.cell-date { color: #6b7280; font-size: 12px; font-variant-numeric: tabular-nums; }
.order-ref { font-weight: 600; color: #111827; }
.cust-name { margin-top: 2px; color: #9ca3af; }
.phone-num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: #4b5563; letter-spacing: -.01em; }

/* confidence — a small badge, color-coded by strength */
.conf-val {
  display: inline-block; padding: 2px 7px; border-radius: 6px;
  font-weight: 700; font-size: 12.5px; font-variant-numeric: tabular-nums;
}
.conf-val.good    { background: #dcfce7; color: #15803d; }
.conf-val.mid     { background: #fef3c7; color: #b45309; }
.conf-val.low     { background: #fee2e2; color: #dc2626; }
.conf-val.neutral { background: #f3f4f6; color: #6b7280; }
.model-tag { display: block; margin-top: 3px; font-size: 10px; color: #b0b6bf; text-transform: uppercase; letter-spacing: .03em; }

/* why cell — tighter, readable evidence lines */
table.rows .why { max-width: 380px; }
table.rows .why .pos,
table.rows .why .neg { font-size: 11.5px; line-height: 1.45; display: flex; gap: 5px; }
table.rows .why .pos { color: #15803d; }
table.rows .why .neg { color: #dc2626; }
table.rows .why .pos + .neg { margin-top: 2px; }
.plain-reason { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 10.5px; color: #b0b6bf; }
.guard-reason { font-size: 12px; color: #6b7280; line-height: 1.35; }

.filters { margin-bottom: 8px; }
.filters button { background: #f3f4f6; border: 0; padding: 5px 12px; border-radius: 20px; margin-right: 6px; cursor: pointer; font-size: 12px; }
.filters button.active { background: #2563eb; color: #fff; }
/* ── Extended filters (search / model / status / confidence) ── */
.xfilters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.xfilters .xsearch { flex: 1 1 240px; min-width: 200px; padding: 7px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; }
.xfilters .xsearch:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.xfilters select { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; background: #fff; cursor: pointer; }
/* custom dropdown */
.dd { position: relative; display: inline-block; }
.dd-btn { display: flex; align-items: center; gap: 8px; min-width: 150px; justify-content: space-between; padding: 7px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; font-size: 13px; color: #1f2937; cursor: pointer; transition: border-color .15s; }
.dd-btn:hover { border-color: #9ca3af; }
.dd.open .dd-btn { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.dd-caret { color: #9ca3af; font-size: 11px; transition: transform .15s; }
.dd.open .dd-caret { transform: rotate(180deg); }
.dd-menu { position: absolute; top: calc(100% + 4px); left: 0; min-width: 100%; background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 8px 24px -6px rgba(0,0,0,.18); padding: 4px; z-index: 50; max-height: 260px; overflow-y: auto; }
.dd-item { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 7px; font-size: 13px; color: #374151; cursor: pointer; white-space: nowrap; }
.dd-item:hover { background: #f3f4f6; }
.dd-item.sel { background: #eff6ff; color: #2563eb; font-weight: 600; }
.dd-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 8px; }
/* confidence range — styled pill */
.conf-range { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6b7280; }
.conf-range .clbl { font-weight: 700; color: #9ca3af; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; }
.conf-inputs { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 4px 8px; }
.conf-inputs:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.conf-inputs .cnum { width: 42px; padding: 3px 4px; border: 0; font-size: 13px; text-align: center; color: #1f2937; -moz-appearance: textfield; }
.conf-inputs .cnum:focus { outline: none; }
.conf-inputs .cnum::-webkit-outer-spin-button, .conf-inputs .cnum::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.conf-inputs .conf-sep { color: #9ca3af; font-size: 11px; }
.conf-inputs .conf-pct { color: #6b7280; font-weight: 600; }
.xfilters .xreset { background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 7px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; }
.xfilters .xreset:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
/* ═══ Unified filter card ═══ */
.filter-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 14px 16px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
.filter-card .frow { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #f3f4f6; }
.filter-card .frow:last-of-type { border-bottom: 0; }
.filter-card .frow.wrap { flex-wrap: wrap; }
.filter-card .frow-label { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .04em; width: 70px; flex: 0 0 70px; }
.filter-card .pills { display: flex; flex-wrap: wrap; gap: 6px; }
.filter-card .pills button { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 12px; border-radius: 20px; cursor: pointer; font-size: 12px; }
.filter-card .pills button.active { background: #2563eb; border-color: #2563eb; color: #fff; }
.fcard-foot { display: flex; align-items: center; gap: 12px; padding-top: 10px; margin-top: 4px; border-top: 1px solid #f3f4f6; flex-wrap: wrap; }
.fcard-foot .date-active { background: #eff6ff; color: #2563eb; border-radius: 8px; padding: 3px 10px; font-size: 12px; }
.reset-all { margin-left: auto; background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; }
.reset-all:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
.result-count { margin: 2px 0 6px; }
.count-line b { color: #1f2937; }
.count-line .dim { color: #9ca3af; }
.trunc-warn { margin-top: 8px; font-size: 12px; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 7px 12px; }
/* colored real-status badge */
.status-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.status-badge.st-green { background: #dcfce7; color: #16a34a; }
.status-badge.st-red { background: #fee2e2; color: #dc2626; }
.status-badge.st-blue { background: #dbeafe; color: #2563eb; }
.status-badge.st-gray { background: #f3f4f6; color: #6b7280; }
.ship-line { margin-top: 0; }
.cancel-reason {
  display: inline-block; max-width: 170px;
  font-size: 11px; font-weight: 600; line-height: 1.3;
  color: #b21e63; background: #fce4f0; border: 1px solid #f6c6de;
  padding: 2px 8px; border-radius: 10px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vpill { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 11px; border-radius: 20px; cursor: pointer; font-size: 12px; }
.vpill.v-right { color: #16a34a; }
.vpill.v-wrong { color: #dc2626; }
.vpill.v-pending { color: #9ca3af; }
.vpill.active { background: #1f2937; color: #fff; border-color: #1f2937; }
.vpill.v-right.active { background: #16a34a; border-color: #16a34a; color: #fff; }
.vpill.v-wrong.active { background: #dc2626; border-color: #dc2626; color: #fff; }

/* ── Polished date filter ── */
.date-bar { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.quick-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.quick-chips button { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 12px; border-radius: 20px; cursor: pointer; font-size: 12px; font-weight: 500; transition: all .15s; }
.quick-chips button:hover { background: #e5e7eb; }
.quick-chips button.on { background: #2563eb; border-color: #2563eb; color: #fff; }
.date-fields { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.date-field { display: flex; flex-direction: column; gap: 2px; }
.date-field .dlabel { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .04em; }
.date-field input[type="date"] { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; color: #1f2937; background: #fff; cursor: pointer; font-family: inherit; transition: border-color .15s; }
.date-field input[type="date"]:hover { border-color: #9ca3af; }
.date-field input[type="date"]:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
/* style the native calendar icon blue + bigger */
.date-field input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer; opacity: .6; filter: invert(35%) sepia(90%) saturate(1500%) hue-rotate(205deg);
  transition: opacity .15s;
}
.date-field input[type="date"]::-webkit-calendar-picker-indicator:hover { opacity: 1; }
.date-fields .arrow { color: #9ca3af; font-size: 16px; margin-top: 14px; }
/* custom-theme the vue2-datepicker to match the page */
.ac-datepicker { width: 260px; }
.ac-datepicker >>> .mx-input { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; box-shadow: none; font-size: 13px; color: #1f2937; padding-left: 12px; }
.ac-datepicker >>> .mx-input:hover { border-color: #9ca3af; }
.ac-datepicker >>> .mx-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.ac-datepicker >>> .mx-icon-calendar, .ac-datepicker >>> .mx-icon-clear { color: #2563eb; }
.btn-apply { background: #2563eb; color: #fff; border: 0; padding: 8px 18px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; margin-top: 14px; }
.btn-apply:hover { background: #1d4ed8; }
.btn-apply:disabled { opacity: .6; cursor: default; }
.btn-clear { background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; margin-top: 14px; }
.btn-clear:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
.date-active { margin-top: 10px; font-size: 12px; color: #6b7280; background: #eff6ff; border-radius: 8px; padding: 6px 10px; display: inline-block; }
.date-active b { color: #2563eb; }
.pill { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.pill.ok { background: #dcfce7; color: #16a34a; }
.pill.warn { background: #fef3c7; color: #b45309; }
.pill.bad { background: #fee2e2; color: #dc2626; }
.muted-pill { background: #f3f4f6; color: #9ca3af; }
.muted { color: #9ca3af; }
.sm { font-size: 11px; }
/* base .why kept as fallback; the polished rules live under table.rows above */
.why { max-width: 320px; }
/* ── Polished pagination bar ── */
.empty-rows { text-align: center; color: #9ca3af; padding: 28px; font-size: 14px; }
.pagination { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #f3f4f6; flex-wrap: wrap; }
.pagination .pg-info { font-size: 13px; color: #4b5563; }
.pagination .pg-info b { color: #1f2937; }
.pagination .pg-controls { display: flex; align-items: center; gap: 4px; }
.pg-btn, .pg-num { min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; cursor: pointer; font-size: 13px; color: #374151; display: inline-flex; align-items: center; justify-content: center; transition: all .12s; }
.pg-btn:hover:not(:disabled), .pg-num:hover { background: #f3f4f6; border-color: #d1d5db; }
.pg-btn:disabled { opacity: .4; cursor: default; }
.pg-num.on { background: #2563eb; border-color: #2563eb; color: #fff; font-weight: 600; }
.pagination .pg-size { display: flex; align-items: center; gap: 4px; }
.pg-size-btn { min-width: 30px; height: 28px; border: 1px solid #e5e7eb; background: #fff; border-radius: 6px; cursor: pointer; font-size: 12px; color: #6b7280; }
.pg-size-btn:hover { background: #f3f4f6; }
.pg-size-btn.on { background: #eff6ff; border-color: #2563eb; color: #2563eb; font-weight: 600; }
@media (max-width: 700px) { .pagination { justify-content: center; } }

/* Your configured threshold (from Settings, per warehouse) */
.whthr-banner { display: flex; align-items: center; gap: 10px; margin: 4px 0 16px; padding: 11px 16px;
  background: linear-gradient(120deg, #ecfdf5, #f0fdf4); border: 1px solid #a7f3d0; border-radius: 12px;
  font-size: 13.5px; color: #334155; }
.whthr-star { font-size: 16px; }
.whthr-val { color: #059669; font-weight: 800; }
.whthr-hint { color: #64748b; font-weight: 400; margin-left: 4px; }
/* address gate banner — amber when the gate is OFF (orders can be confirmed with no address) */
.whthr-banner.whaddr-off { background: linear-gradient(120deg, #fffbeb, #fef3c7); border-color: #fcd34d; }
.whaddr-val-off { color: #b45309; font-weight: 800; }
.thr tr.thr-selected td { background: #ecfdf5; font-weight: 700; }
.thr tr.thr-selected td:first-child { box-shadow: inset 3px 0 0 #059669; }
.thr-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #059669;
  background: #d1fae5; border: 1px solid #a7f3d0; padding: 1px 6px; border-radius: 20px; margin-left: 6px; }
</style>

<!-- non-scoped: the datepicker popup renders at <body> root, so it can't be scoped -->
<style>
.mx-datepicker-main { border-radius: 12px !important; box-shadow: 0 12px 32px -6px rgba(0,0,0,.2) !important; border: 1px solid #e5e7eb !important; font-family: inherit; }
.mx-datepicker-main .mx-calendar-content .cell.active { background: #2563eb !important; border-radius: 8px; }
.mx-datepicker-main .mx-calendar-content .cell.in-range { background: #eff6ff !important; color: #2563eb !important; }
.mx-datepicker-main .mx-calendar-content .cell.hover-in-range { background: #dbeafe !important; }
.mx-datepicker-main .mx-calendar-content .cell:not(.disabled):hover { background: #dbeafe !important; border-radius: 8px; }
.mx-datepicker-main .mx-btn:hover { color: #2563eb !important; border-color: #2563eb !important; }
.mx-datepicker-main .mx-table-date td.today { color: #2563eb !important; font-weight: 700; }
</style>
